import {
  CALENDAR_UNITS,
  CHECKLIST_ITEM_KINDS,
  INTERVAL_BASES,
  JOB_PRIORITIES,
  JOB_TYPES,
  WORK_TYPES,
} from '@felix/domain';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Alert,
  Button,
  Drawer,
  Group,
  MultiSelect,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconInfoCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import {
  Controller,
  type FieldPath,
  type UseFormReturn,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DayInput } from '../../inputs/DayInput.js';
import { useUnsavedChanges } from '../../shell-context.js';
import type { MeasurementPoint, MeasurementPointInput } from './condition-types.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import { IntervalHistory } from './IntervalHistory.js';
import { JobChangeLog } from './JobChangeLog.js';
import { emptyJobForm, type JobFormValues, jobFormOf, jobFormSchema } from './job-form.js';
import { MeasurementPointEditor } from './MeasurementPointEditor.js';
import classes from './Pms.module.css';
import work from './PmsWork.module.css';
import { PmsBadge } from './PmsWorkParts.js';
import { descendantSet, type JobActions } from './plan-view.js';
import type {
  EquipmentNode,
  InstructionOption,
  IntervalHistoryEntry,
  JobChangeEntry,
  JobView,
  PlanItem,
} from './types.js';

export interface JobCardDrawerProps {
  opened: boolean;
  /** `null`: yeni iş. */
  job: JobView | null;
  plan: PlanItem | null;
  /** Yeni işin önerilen ekipmanı. */
  equipmentId: string | null;
  nodes: readonly EquipmentNode[];
  today: string;
  actions: JobActions;
  instructions: readonly InstructionOption[];
  onClose: () => void;
  onSave: (values: JobFormValues, job: JobView | null) => Promise<void>;
  onRequestInterval?: (job: JobView) => void;
  /** Etkin gemide ofisin aralık kararı: "Change interval" (yalnız web; ADR 0104). */
  onOfficeInterval?: (job: JobView) => void;
  /** İşin aralık geçmişi (ADR 0104 §7); boşsa bölüm çizilmez. */
  intervalHistory?: readonly IntervalHistoryEntry[];
  /** Aralık ve talimat değişiklik kaydı rolüyle (DNV-CP-0206 4.6 b); boşsa çizilmez. */
  changeLog?: readonly JobChangeEntry[];
  /** Ölçüm noktaları ve limit düzenleyici (M7c; Task 21a). */
  measurement?: JobMeasurementProps;
}

/** Job Card'ın ölçüm noktası bölümü: geminin noktaları ve yazma yetkisi (K1, K20). */
export interface JobMeasurementProps {
  points: readonly MeasurementPoint[];
  can: { create: boolean; edit: boolean; delete: boolean };
  onCreate: (input: MeasurementPointInput) => Promise<unknown>;
  onUpdate: (point: MeasurementPoint, patch: Partial<MeasurementPointInput>) => Promise<unknown>;
  onRemove: (point: MeasurementPoint, reason: string) => Promise<unknown>;
}

/**
 * Job Card çekmecesi (PMS belgesi §8–§10; plan M7 Task 14a): kimlik, aralık ve dayanağı, prosedür,
 * kontrol listesi, güvenlik maddeleri, kabul ölçütü, talimat bağlantısı (K16, K38) ve önceki
 * sistemdeki son yapılış (K12). Aralık kayıt yokken gerekçeyle düzeltilir; kayıttan sonra
 * "Request interval change" (K11). Ofiste etkinleşmiş gemide salt okunur (K1).
 */
export function JobCardDrawer(props: JobCardDrawerProps) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const { job, opened, actions } = props;
  const readOnly = job ? !actions.edit : !actions.create;
  const intervalReason = job !== null && actions.intervalDirect;
  const [error, setError] = useState<unknown>(null);
  const form = useForm<JobFormValues>({
    resolver: zodResolver(
      jobFormSchema({ today: props.today, intervalReason, ...(job ? { job } : {}) }),
    ),
    defaultValues: emptyJobForm(props.equipmentId ?? ''),
  });
  const { isSubmitting, isDirty } = form.formState;
  useUnsavedChanges(opened && isDirty);

  useEffect(() => {
    if (!opened) return;
    setError(null);
    form.reset(job ? jobFormOf(job) : emptyJobForm(props.equipmentId ?? ''));
  }, [opened, job, props.equipmentId, form]);

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await props.onSave(values, job);
    } catch (failure) {
      setError(failure);
    }
  });

  return (
    <Drawer
      opened={opened}
      onClose={props.onClose}
      position="right"
      size={640}
      title={
        <span className={classes.drawerTitle}>
          {job ? t('pms.work.job.title', { code: job.code }) : t('pms.work.job.new')}
        </span>
      }
      closeButtonProps={{ 'aria-label': t('pms.drawer.close') }}
    >
      <form onSubmit={submit} className={classes.drawerForm} noValidate>
        <JobCardForm
          form={form}
          job={job}
          plan={props.plan}
          nodes={props.nodes}
          today={props.today}
          actions={actions}
          readOnly={readOnly}
          intervalReason={intervalReason}
          instructions={props.instructions}
          {...(props.onRequestInterval ? { onRequestInterval: props.onRequestInterval } : {})}
          {...(props.onOfficeInterval ? { onOfficeInterval: props.onOfficeInterval } : {})}
          {...(props.intervalHistory ? { intervalHistory: props.intervalHistory } : {})}
          {...(props.changeLog ? { changeLog: props.changeLog } : {})}
          {...(props.measurement ? { measurement: props.measurement } : {})}
        />
        {error ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert">
            {errorText(error)}
          </Alert>
        ) : null}
        <div className={classes.drawerFooter}>
          <Button variant="default" onClick={props.onClose}>
            {readOnly ? t('pms.work.close') : t('pms.drawer.cancel')}
          </Button>
          {readOnly ? null : (
            <Button type="submit" loading={isSubmitting}>
              {t('pms.drawer.save')}
            </Button>
          )}
        </div>
      </form>
    </Drawer>
  );
}

export interface JobCardFormProps {
  form: UseFormReturn<JobFormValues>;
  job: JobView | null;
  plan: PlanItem | null;
  nodes: readonly EquipmentNode[];
  today: string;
  actions: JobActions;
  readOnly: boolean;
  intervalReason: boolean;
  instructions: readonly InstructionOption[];
  onRequestInterval?: (job: JobView) => void;
  onOfficeInterval?: (job: JobView) => void;
  intervalHistory?: readonly IntervalHistoryEntry[];
  changeLog?: readonly JobChangeEntry[];
  measurement?: JobMeasurementProps;
}

/** Formun gövdesi: çekmeceden ayrı (bileşen testi sunucu çizimiyle). */
export function JobCardForm({
  form,
  job,
  plan,
  nodes,
  today,
  actions,
  readOnly,
  intervalReason,
  instructions,
  onRequestInterval,
  onOfficeInterval,
  intervalHistory,
  changeLog,
  measurement,
}: JobCardFormProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) => t(`pms.work.job.${key}`, values);
  const errors = form.formState.errors;
  const fieldError = (name: FieldPath<JobFormValues>) => {
    let node: unknown = errors;
    for (const part of name.split('.'))
      node = (node as Record<string, unknown> | undefined)?.[part];
    const message = (node as { message?: string } | undefined)?.message;
    return message
      ? t(`pms.fieldErrors.${message}`, { defaultValue: t('pms.errors.VALIDATION_FAILED') })
      : undefined;
  };
  const intervalLocked = readOnly || (job !== null && !actions.intervalDirect);
  const procedure = useFieldArray({ control: form.control, name: 'procedure' });
  const checklist = useFieldArray({ control: form.control, name: 'checklist' });
  const safety = useFieldArray({ control: form.control, name: 'safetyItems' });
  const options = <T extends readonly string[]>(values: T, prefix: string) =>
    values.map((value) => ({ value, label: t(`${prefix}.${value}`, { defaultValue: value }) }));
  const select = (
    name: FieldPath<JobFormValues>,
    label: string,
    data: { value: string; label: string }[],
    extra: { required?: boolean; disabled?: boolean; clearable?: boolean } = {},
  ) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <Select
          label={label}
          required={extra.required === true}
          data={data}
          value={(field.value as string | null) || null}
          onChange={(value) => field.onChange(value ?? '')}
          allowDeselect={extra.clearable === true}
          clearable={extra.clearable === true}
          clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
          readOnly={readOnly || extra.disabled === true}
          error={fieldError(name)}
        />
      )}
    />
  );
  const number = (
    name: 'calendarEvery' | 'runningHours' | 'estimatedMinutes' | 'baselineDoneHours',
    label: string,
    locked: boolean,
  ) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <NumberInput
          label={label}
          min={0}
          decimalScale={name === 'calendarEvery' || name === 'estimatedMinutes' ? 0 : 1}
          value={field.value as number | ''}
          onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
          onBlur={field.onBlur}
          readOnly={locked}
          error={fieldError(name)}
        />
      )}
    />
  );
  const hasRecord = plan?.lastDone.source === 'record';
  const equipmentId = form.watch('equipmentId');
  // Tetikleyici noktalar: işin ekipmanının ve alt bileşenlerinin noktaları (K21)
  const triggerOptions = (() => {
    if (!measurement || !equipmentId) return [];
    const subtree = descendantSet(nodes, equipmentId);
    return measurement.points
      .filter((p) => p.deletedAt === null && subtree.has(p.equipmentId))
      .map((p) => ({ value: p.id, label: `${p.name} (${p.unit})` }));
  })();
  return (
    <Stack gap="md">
      {actions.editedOnBoard ? (
        <Alert color="blue" icon={<IconInfoCircle size={16} />}>
          {t('pms.equipment.details.editedOnBoard')}
        </Alert>
      ) : null}
      <fieldset className={classes.fieldset}>
        <legend>{k('sections.identity')}</legend>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput
            label={k('code')}
            required
            readOnly={readOnly}
            {...form.register('code')}
            error={fieldError('code')}
          />
          {select(
            'equipmentId',
            k('equipment'),
            nodes.map((n) => ({ value: n.id, label: `${n.name} · ${n.code}` })),
            { required: true, disabled: job !== null },
          )}
        </SimpleGrid>
        <TextInput
          label={k('jobTitle')}
          required
          readOnly={readOnly}
          {...form.register('title')}
          error={fieldError('title')}
          mt="sm"
        />
        <SimpleGrid cols={{ base: 1, sm: 3 }} mt="sm">
          {select('workType', k('workType'), options(WORK_TYPES, 'pms.work.workTypes'))}
          {select('jobType', k('jobType'), options(JOB_TYPES, 'pms.work.jobTypes'))}
          {select('priority', k('priority'), options(JOB_PRIORITIES, 'pms.work.priority'))}
        </SimpleGrid>
      </fieldset>

      <fieldset className={classes.fieldset}>
        <legend>{k('sections.interval')}</legend>
        {plan?.intervalChangePending ? (
          <PmsBadge status="pending">{t('pms.work.plan.intervalPending')}</PmsBadge>
        ) : null}
        <SimpleGrid cols={{ base: 1, sm: 3 }} mt="xs">
          {number('calendarEvery', k('calendarEvery'), intervalLocked)}
          <Controller
            control={form.control}
            name="calendarUnit"
            render={({ field }) => (
              <Select
                label={k('calendarUnit')}
                data={options(CALENDAR_UNITS, 'pms.work.units')}
                value={field.value}
                onChange={(value) => field.onChange(value ?? 'month')}
                allowDeselect={false}
                readOnly={intervalLocked}
              />
            )}
          />
          {number('runningHours', k('runningHours'), intervalLocked)}
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, sm: 2 }} mt="sm">
          {select('intervalBasis', k('intervalBasis'), options(INTERVAL_BASES, 'pms.work.bases'), {
            disabled: intervalLocked,
          })}
          {number('estimatedMinutes', k('estimatedMinutes'), readOnly)}
        </SimpleGrid>
        {intervalReason && !readOnly ? (
          <Textarea
            label={k('changeReason')}
            description={k('changeReasonHint')}
            autosize
            minRows={2}
            mt="sm"
            {...form.register('changeReason')}
            error={fieldError('changeReason')}
          />
        ) : null}
        {triggerOptions.length > 0 ? (
          <Controller
            control={form.control}
            name="conditionPointIds"
            render={({ field }) => (
              <MultiSelect
                mt="sm"
                label={t('pms.condition.editor.triggers')}
                description={t('pms.condition.editor.triggersHint')}
                data={triggerOptions}
                value={field.value}
                onChange={field.onChange}
                readOnly={intervalLocked}
                clearable={!intervalLocked}
                error={fieldError('conditionPointIds')}
              />
            )}
          />
        ) : null}
        {job && hasRecord && !actions.intervalDirect && !actions.officeInterval ? (
          <p className={work.note}>{k('intervalLocked')}</p>
        ) : null}
        {job && actions.officeInterval && onOfficeInterval ? (
          <>
            <p className={work.note}>
              {k(plan?.intervalChangePending ? 'officeIntervalBlocked' : 'officeIntervalHint')}
            </p>
            <Button
              variant="default"
              size="xs"
              mt="sm"
              disabled={plan?.intervalChangePending === true}
              onClick={() => onOfficeInterval(job)}
            >
              {k('officeInterval')}
            </Button>
          </>
        ) : null}
        {job && actions.requestInterval && onRequestInterval ? (
          <Button
            variant="default"
            size="xs"
            mt="sm"
            disabled={plan?.intervalChangePending === true}
            onClick={() => onRequestInterval(job)}
          >
            {k('requestInterval')}
          </Button>
        ) : null}
      </fieldset>

      {job && intervalHistory ? <IntervalHistory entries={intervalHistory} /> : null}
      {job && changeLog ? <JobChangeLog entries={changeLog} /> : null}

      <fieldset className={classes.fieldset}>
        <legend>{k('sections.procedure')}</legend>
        <ol className={work.listEditor}>
          {procedure.fields.map((item, index) => (
            <li key={item.id}>
              <TextInput
                aria-label={k('step', { n: index + 1 })}
                readOnly={readOnly}
                {...form.register(`procedure.${index}.text`)}
                error={fieldError(`procedure.${index}.text`)}
              />
              {readOnly ? null : (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label={k('removeStep', { n: index + 1 })}
                  onClick={() => procedure.remove(index)}
                >
                  <IconTrash size={15} />
                </ActionIcon>
              )}
            </li>
          ))}
        </ol>
        {readOnly ? null : (
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => procedure.append({ text: '' })}
          >
            {k('addStep')}
          </Button>
        )}
      </fieldset>

      <fieldset className={classes.fieldset}>
        <legend>{k('sections.checklist')}</legend>
        <ol className={work.listEditor}>
          {checklist.fields.map((item, index) => (
            <li key={item.id} className={work.checkRow}>
              <TextInput
                aria-label={k('checkItem', { n: index + 1 })}
                readOnly={readOnly}
                {...form.register(`checklist.${index}.text`)}
                error={fieldError(`checklist.${index}.text`)}
              />
              <Controller
                control={form.control}
                name={`checklist.${index}.kind`}
                render={({ field }) => (
                  <Select
                    aria-label={k('checkKind', { n: index + 1 })}
                    data={options(CHECKLIST_ITEM_KINDS, 'pms.work.job.kinds')}
                    value={field.value}
                    onChange={(value) => field.onChange(value ?? 'check')}
                    allowDeselect={false}
                    readOnly={readOnly}
                    className={work.kindSelect}
                  />
                )}
              />
              <TextInput
                aria-label={k('checkUnit', { n: index + 1 })}
                placeholder={k('unit')}
                readOnly={readOnly}
                className={work.unitInput}
                {...form.register(`checklist.${index}.unit`)}
              />
              {readOnly ? null : (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label={k('removeCheck', { n: index + 1 })}
                  onClick={() => checklist.remove(index)}
                >
                  <IconTrash size={15} />
                </ActionIcon>
              )}
            </li>
          ))}
        </ol>
        {readOnly ? null : (
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => checklist.append({ text: '', kind: 'check', unit: '' })}
          >
            {k('addCheck')}
          </Button>
        )}
      </fieldset>

      {measurement ? (
        <MeasurementPointEditor
          jobId={job?.id ?? null}
          equipmentId={job?.equipmentId ?? equipmentId}
          points={measurement.points}
          can={readOnly ? { create: false, edit: false, delete: false } : measurement.can}
          today={today}
          onCreate={measurement.onCreate}
          onUpdate={measurement.onUpdate}
          onRemove={measurement.onRemove}
        />
      ) : null}

      <fieldset className={classes.fieldset}>
        <legend>{k('sections.safety')}</legend>
        <ol className={work.listEditor}>
          {safety.fields.map((item, index) => (
            <li key={item.id}>
              <TextInput
                aria-label={k('safetyItem', { n: index + 1 })}
                readOnly={readOnly}
                {...form.register(`safetyItems.${index}.text`)}
                error={fieldError(`safetyItems.${index}.text`)}
              />
              {readOnly ? null : (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label={k('removeSafety', { n: index + 1 })}
                  onClick={() => safety.remove(index)}
                >
                  <IconTrash size={15} />
                </ActionIcon>
              )}
            </li>
          ))}
        </ol>
        {readOnly ? null : (
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => safety.append({ text: '' })}
          >
            {k('addSafety')}
          </Button>
        )}
        <Textarea
          label={k('acceptance')}
          autosize
          minRows={2}
          mt="sm"
          readOnly={readOnly}
          {...form.register('acceptanceCriteria')}
          error={fieldError('acceptanceCriteria')}
        />
      </fieldset>

      <fieldset className={classes.fieldset}>
        <legend>{k('sections.documents')}</legend>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {select(
            'instructionDocumentId',
            k('instruction'),
            instructions.map((d) => ({ value: d.id, label: `${d.docNo} · ${d.title}` })),
            { clearable: true },
          )}
          <TextInput
            label={k('manualRef')}
            readOnly={readOnly}
            {...form.register('manualRef')}
            error={fieldError('manualRef')}
          />
        </SimpleGrid>
        <Controller
          control={form.control}
          name="ceSurvey"
          render={({ field }) => (
            <Switch
              mt="sm"
              label={k('ceSurvey')}
              checked={field.value}
              disabled={readOnly}
              onChange={(event) => field.onChange(event.currentTarget.checked)}
            />
          )}
        />
        {/* UMS / E0–ECO alarm testi (DNV-CP-0206 Sec.2 [4.3] d; ADR 0133 §3) */}
        <Controller
          control={form.control}
          name="alarmTest"
          render={({ field }) => (
            <Switch
              mt="sm"
              label={k('alarmTest')}
              description={k('alarmTestHint')}
              checked={field.value}
              disabled={readOnly}
              onChange={(event) => field.onChange(event.currentTarget.checked)}
            />
          )}
        />
      </fieldset>

      {!hasRecord ? (
        <fieldset className={classes.fieldset}>
          <legend>{k('sections.baseline')}</legend>
          <p className={work.note}>{k('baselineHint')}</p>
          <Group grow align="flex-start">
            <Controller
              control={form.control}
              name="baselineDoneOn"
              render={({ field }) => (
                <DayInput
                  label={k('baselineDoneOn')}
                  value={field.value || null}
                  onChange={(value) => field.onChange(value ?? '')}
                  maxDate={today}
                  readOnly={readOnly}
                  clearable
                  clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
                  error={fieldError('baselineDoneOn')}
                />
              )}
            />
            {number('baselineDoneHours', k('baselineDoneHours'), readOnly)}
          </Group>
        </fieldset>
      ) : null}
    </Stack>
  );
}
